import { AbiCoder, defaultAbiCoder } from "@ethersproject/abi"
import type { AbiInput, AbiItem } from "web3-utils"

import ERC20_ABI from "createProposal/helpers/abis/erc20.abi"
import ERC721_ABI from "createProposal/helpers/abis/erc721.abi"
import ERC721_AUTO_INCREMENT_IDS_ABI from "createProposal/helpers/abis/erc721-auto-increment-ids.abi"
import ERC1155_ABI from "createProposal/helpers/abis/erc1155.abi"
import OZ_GOVERNOR_ABI from "createProposal/helpers/abis/oz-governor.abi"
import OWNABLE_ABI from "createProposal/helpers/abis/ownable.abi"
import ROLES_ABI from "createProposal/helpers/abis/roles.abi"
import PAUSABLE_ABI from "createProposal/helpers/abis/pausable.abi"
import GNOSIS_SAFE_IAVATAR_ABI from "createProposal/helpers/abis/gnosis-safe-iavatar.abi"
import UPGRADEABILITY_UUPS_ABI from "createProposal/helpers/abis/upgradeability-uups.abi"
import SUDOSWAP_PAIR_FACTORY_ABI from "createProposal/helpers/abis/sudoswap.abi"
import GNOSIS_SAFE_L2_ABI from "createProposal/helpers/abis/gnosis-safe-l2.abi"
import type { AbiInputWithValue, AbiType } from "createProposal/types/abi"
import { jsonParse } from "common/helpers/serialization"
import { getValidJsonString } from "common/helpers/string"

export const encodeAbiInput = (
  abiInputWithValue: AbiInputWithValue,
): string => {
  const { components, type, value } = abiInputWithValue

  return encodeComplexField(components, type, value)
}

// For complex types like tuple[], tuple, arrays of simple types etc..
export const encodeComplexField = (
  components: AbiInput[] | undefined,
  type: string,
  value: string,
) => {
  if (!type || !value) return ""

  if (type === "tuple[]") {
    const nextType = getNextType(type, components)

    return defaultAbiCoder.encode(
      [nextType],
      [jsonParse(getValidJsonString(value))],
    )
  }

  if (type === "tuple") {
    const nextType = getNextType(type, components)

    return defaultAbiCoder.encode(
      [nextType],
      [jsonParse(getValidJsonString(value))],
    )
  }

  const shouldParseValue = type.includes("[]")

  const nextValue = shouldParseValue ? jsonParse(value) : value

  return encodeSimpleField(type, nextValue)
}

// For simple types like uint256, bool, etc..
export const encodeSimpleField = (type: string, value: string) => {
  const abi = new AbiCoder()

  return abi.encode([type], [value])
}

export const getNextType = (
  type: string,
  components: AbiInput[] | undefined,
): string => {
  if (type === "tuple") {
    return `${type}(${components
      ?.map((abiInput) => getNextType(abiInput.type, abiInput.components))
      .join(",")})`
  }

  if (type === "tuple[]") {
    return `tuple[(${components
      ?.map((abiInput) => getNextType(abiInput.type, abiInput.components))
      .join(",")})]`
  }

  return type
}

export const filterAbiMethodsForProposal = (
  abiMethods: AbiItem[],
): AbiItem[] => {
  return abiMethods.filter(
    (abiMethod: AbiItem) =>
      abiMethod.type === "function" &&
      abiMethod.stateMutability !== "view" &&
      abiMethod.stateMutability !== "pure",
  )
}

export const getAbiTypes = (): AbiType[] => {
  return [
    { name: "ERC-20", abi: ERC20_ABI },
    { name: "ERC-721", abi: ERC721_ABI },
    {
      name: "ERC-721 (Auto Increment Ids)",
      abi: ERC721_AUTO_INCREMENT_IDS_ABI,
    },
    { name: "ERC-1155", abi: ERC1155_ABI },
    { name: "OZ Governor", abi: OZ_GOVERNOR_ABI },
    { name: "Ownable", abi: OWNABLE_ABI },
    { name: "Roles", abi: ROLES_ABI },
    { name: "Pausable", abi: PAUSABLE_ABI },
    { name: "IAvatar (Gnosis Safe)", abi: GNOSIS_SAFE_IAVATAR_ABI },
    { name: "GnosisSafeL2", abi: GNOSIS_SAFE_L2_ABI },
    { name: "Upgradeability (UUPS)", abi: UPGRADEABILITY_UUPS_ABI },
    { name: "sudoswap: Pair Factory", abi: SUDOSWAP_PAIR_FACTORY_ABI },
  ]
}
